'use client';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import { getImageProps } from 'next/image';
import { useRouter } from 'next/navigation';
import React from 'react';

import LinkCustom from '@/app/_components/link/LinkCustom';
import { DateService } from '@/services/date.service';
import { directusImageLoader } from '@/utils/loaders';

import Styles from './Article.module.scss';
import Mosaics from './Mosaics';
import NewsCard from './NewsCard';

type BlogsProps = {
  date_to_publish: string;
  mainImage: string | null;
  headerTitle: string;
  slug: string;
}[];

type ArticleProps = {
  headerTitle: string;
  date_to_publish: string;
  headerContent: string;
  mainImage: string | null;
  sections: {
    sectionContent: string;
    sectionImage: string | null;
  }[];
  mosaics: {
    description: string;
    image_id: string;
    title: string;
  }[];
  bodyContent: string | null;
};

function Article({
  latestNewsBlogs,
  article,
}: {
  latestNewsBlogs: BlogsProps;
  article: ArticleProps;
}) {
  const router = useRouter();
  const headerCommon = {
    alt: 'Hero',
    width: 664,
    height: 406,
    loader: directusImageLoader,
  };
  const imageKeys = {
    mobileKey: 'blog-banner-mobile',
    desktopKey: 'blog-banner-desktop',
  };
  const imageKeysBody = {
    mobileKey: 'blog-body-mobile',
    desktopKey: 'blog-body-desktop',
  };

  const {
    props: { srcSet: headerMobile },
  } = getImageProps({
    ...headerCommon,
    src: `${article?.sections[0] && article?.sections[0].sectionImage ? article?.sections[0]?.sectionImage + `?key=${imageKeys.mobileKey}` : ''}`,
  });
  const {
    props: { srcSet: headerDesktop, ...headerRest },
  } = getImageProps({
    ...headerCommon,
    src: `${(article?.sections[0] && article?.sections[0].sectionImage && article?.sections[0].sectionImage + `?key=${imageKeys.desktopKey}`) ?? ''}`,
  });

  const {
    props: { srcSet: bodyMobile },
  } = getImageProps({
    ...headerCommon,
    src: `${article?.sections[1] && article?.sections[1].sectionImage ? article?.sections[1]?.sectionImage + `?key=${imageKeys.mobileKey}` : ''}`,
  });
  const {
    props: { srcSet: bodyDesktop, ...bodyRest },
  } = getImageProps({
    ...headerCommon,
    src: `${article?.sections[1] && article?.sections[1].sectionImage ? article?.sections[1]?.sectionImage + `?key=${imageKeysBody.desktopKey}` : ''}`,
  });

  const {
    props: { srcSet: footerMobile },
  } = getImageProps({
    ...headerCommon,
    src: `${article?.sections[2] && article?.sections[2].sectionImage ? article?.sections[2]?.sectionImage + `?key=${imageKeys.mobileKey}` : ''}`,
  });
  const {
    props: { srcSet: footerDesktop, ...footerRest },
  } = getImageProps({
    ...headerCommon,
    src: `${article?.sections[2] && article?.sections[2]?.sectionImage ? article?.sections[2]?.sectionImage + `?key=${imageKeys.desktopKey}` : ''}`,
  });

  return (
    <Box className={Styles.articlePageWrapper}>
      <Container
        maxW={1328}
        width={'100%'}
        marginX={'auto'}
        paddingX={{ base: 6, '2xl': 0 }}
      >
        <Box className={Styles.mainNav} mb={14}>
          <Button
            className={Styles.backBtn}
            padding={0}
            role="button"
            marginRight={6}
            onClick={() => router.back()}
          >
            <ChevronLeftIcon role="button" />
            <Box className={Styles.backBtnText} role="button">
              Back
            </Box>
          </Button>
        </Box>

        <Box mb={12}>
          <Box className={Styles.headingElement}>
            <Heading
              as="h1"
              textTransform={'capitalize'}
              className={Styles.titleLg}
              marginBottom={3}
            >
              {article?.headerTitle}
            </Heading>
            {article?.date_to_publish && (
              <Text fontSize="md">
                {DateService.formatDate(article?.date_to_publish)}
              </Text>
            )}
          </Box>
        </Box>
        {article?.sections[0]?.sectionImage && (
          <Box className={Styles.heroImageWrapper}>
            <Box className={Styles.heroImageBlock} mb={12}>
              <picture className={Styles.heroImage}>
                <source media="(max-width: 575px)" srcSet={headerMobile} />
                <source media="(min-width: 576px)" srcSet={headerDesktop} />
                <img {...headerRest} alt="hero-banner-image" />
              </picture>
            </Box>
            <Box maxW={750} width={'100%'} mb={12}>
              <div
                dangerouslySetInnerHTML={{
                  __html: article?.headerContent,
                }}
              ></div>
            </Box>
          </Box>
        )}
        {article?.sections[1]?.sectionImage && (
          <SimpleGrid columns={[1, 1, 2, 2]} gap={12} paddingY={14}>
            <Box maxW={664} width={'100%'} mb={{ base: 8, md: 0 }}>
              <picture>
                <source media="(max-width: 575px)" srcSet={bodyMobile} />
                <source media="(min-width: 576px)" srcSet={bodyDesktop} />
                <img {...bodyRest} alt="hero-banner-image" />
              </picture>
            </Box>
            <Box>
              <div
                dangerouslySetInnerHTML={{
                  __html: article?.sections[1]?.sectionContent,
                }}
              ></div>
            </Box>
          </SimpleGrid>
        )}
      </Container>
      {article?.mosaics?.length > 0 && <Mosaics article={article} />}

      {article?.sections[2]?.sectionImage && (
        <Container
          maxW={1328}
          width={'100%'}
          marginX={'auto'}
          paddingX={{ base: 6, '2xl': 0 }}
        >
          <Box
            className={Styles.heroImageWrapper}
            paddingTop={12}
            paddingBottom={'50px'}
          >
            <Box maxW={750} width={'100%'} mb={12}>
              <div
                dangerouslySetInnerHTML={{
                  __html: article?.sections[2]?.sectionContent,
                }}
              ></div>
            </Box>
            <Box className={Styles.heroImageBlock}>
              <picture className={Styles.heroImage}>
                <source media="(max-width: 575px)" srcSet={footerMobile} />
                <source media="(min-width: 576px)" srcSet={footerDesktop} />
                <img {...footerRest} alt="hero-banner-image" />
              </picture>
            </Box>
          </Box>
        </Container>
      )}
      {article?.bodyContent ? (
        <Container
          maxW={1328}
          width={'100%'}
          marginX={'auto'}
          paddingX={{ base: 6, '2xl': 0 }}
          paddingBottom={'100px'}
        >
          <Box
            dangerouslySetInnerHTML={{
              __html: article?.bodyContent,
            }}
          />
        </Container>
      ) : null}
      <Box
        className={Styles.latestNewsWrapper}
        paddingY={{ base: 20, sm: '100px' }}
      >
        <Container maxW={1328} width={'100%'} paddingX={{ base: 6, '2xl': 0 }}>
          <Flex
            direction={{ base: 'column', md: 'row' }}
            flexWrap={'wrap'}
            alignItems={{ base: 'start', md: 'center' }}
            marginBottom={{ base: 16, md: 10 }}
            className={Styles.latestNewsHeader}
          >
            <Box className={Styles.headingElement}>
              <Text fontSize="xs" color={'white'} marginBottom={4}>
                Read our latest articles for tips and advice
              </Text>
              <Heading
                as="h2"
                textTransform={'capitalize'}
                className={Styles.titleLg}
                color={'white'}
              >
                Continue Reading
              </Heading>
            </Box>
            <Box
              mt={{ base: 8, md: 0 }}
              marginLeft={{ md: 'auto' }}
              className={Styles.latestNewsHeaderCTA}
            >
              <LinkCustom
                role="link"
                aria-label="View All"
                className="btnOutlineWhite"
                href="/blog/latest-news"
              >
                View all
                <ChevronRightIcon width={4} height={4} marginLeft={3} />
              </LinkCustom>
            </Box>
          </Flex>
          <SimpleGrid columns={[1, 1, 2, 3]} gap={10}>
            {latestNewsBlogs?.map((blog, i) => (
              <NewsCard
                key={i}
                image={blog?.mainImage}
                title={blog?.headerTitle}
                date_to_publish={blog?.date_to_publish}
                slug={blog?.slug ?? ''}
              />
            ))}
          </SimpleGrid>
        </Container>
      </Box>
    </Box>
  );
}

export default Article;
